/**
 * Operating expense by line and the L13 programs (07.3, `opex.lines@1`).
 *   07.3 E1   h(a) = ½ for 0 ≤ a < 12, 1 for a ≥ 12, 0 for a < 0 (phase-in by programme age)
 *   07.3 E2a  c̃_p = c_p · ρ^k for a cut (c_p < 0), k = cut programmes before p; else c_p (owner M10); a fine-only programme's c̃ is its record's `changeRate`
 *   07.3 E10  a cut lands at its realised share: c̃_p · (1 − cb_p(a)), the creep-back cb_p(a) = (1 − r_level) · clamp((a − 12) ÷ (36 − 12), 0, 1) (P4-D7)
 *   07.3 E2   φ = Π_p [1 + c̃_p·(1 − cb_p)·h(m − start_p)]
 *   07.3 E3a  I(m) = Π (1 + κ·CPI(k))^(1/12): the price level the programme lines are paid at (R1)
 *   07.3 E3   comp, occupancy, other = [L0·(1 + κ on comp) + (Tr − Tr0) on comp]·σ^ε·I·φ·(1 − e_eff) ÷ 12 + c^br(ΔN)·φ·(1 − e_eff) ÷ 12 on occupancy
 *             (ε = `opexBalanceElasticityRate` while σ ≥ 1, `opexDownwardElasticityRate` below; training sits inside σ and φ, as the oracle
 *             scripts/calib/models/ch07.py opex_month and the G7 L16-comp rows scale it; the doc's E3 puts it outside)
 *   07.3 E4   technology = Σ TI_θ·σ ÷ 12; marketing = Σ M_k ÷ 12
 *   07.3 E5   cdi = min(A_cdi ÷ 12, remaining intangibles_cdi)
 *   07.3 E6   restructuring = R_1 in a cut programme's first month; branchOpening = b^open · cpb · N^open
 *   07.3 E9   an Invest programme tops up the efficiency-and-data theme for its first 12 months: `investTechTopUpRateByLevel` × that theme's spend, in the technology line
 *   07.3 E7   leaseExit = the lease-exit charge × the branches a round's closure closes (Gate 3d)
 *   07.3 E13  a Deep programme's operational losses, $ a year for `operationalLossMonths` from its start, in the `events` line
 *   07.3 E14  the management rating sits one notch down for N quarter-ends from a programme's start (`managementNotchHeld`; 09.1 reads it)
 * σ = franchise.opexBalanceScale. Money is rounded once, per line. The comp line is the pay outside the RM and loan-officer teams: their pay is
 * headcount-driven (03.5 E4) and posts with their hiring (`steps/teams.ts`), so the engine hands this model the pay line net of it.
 */
import { roundToUsd, sortedEntries, type BankState, type OpexModel, type OpexParams, type Usd } from "@bsim/domain";

type CostCutChannels = NonNullable<OpexParams["costCutChannels"]>;
type Programmes = NonNullable<BankState["franchise"]["opexPrograms"]>;

const PHASE_IN_MONTHS = 12;
const PHASE_IN_SHARE = 0.5;
const MONTHS_PER_ROUND = 12;

/** E2a: the run-rate change a programme achieves: a cut finds less each time (c·ρ^k, k = the cuts before it); any other, and a fine-only programme's own, is c. */
const achieved = (c: number, changeRate: number | undefined, cutsSoFar: number, rho: number): number => (c < 0 && changeRate === undefined ? c * rho ** cutsSoFar : c);

/** E10: the share of a cut's saving that has crept back `age` months after its start: none before `creepBackStartMonth`, then evenly to 1 − r (its level's realised share) by `creepBackEndMonth`. */
function creepBackShare(channels: CostCutChannels | undefined, level: string, age: number): number {
  if (channels === undefined) return 0;
  const lost = 1 - (channels.realisedShareByLevel[level] ?? 1);
  return lost * Math.min(1, Math.max(0, (age - channels.creepBackStartMonth) / (channels.creepBackEndMonth - channels.creepBackStartMonth)));
}

/** E1 × E2 × E10: the run-rate multiplier φ of every programme in force at `month`, and whether a cut programme starts in it. */
function programmes(bank: BankState, params: OpexParams, month: number): { readonly phi: number; readonly cutStartsNow: boolean } {
  const rho = params.repeatProgramSavingsRetentionRate ?? 1;
  let phi = 1;
  let cutsSoFar = 0;
  let cutStartsNow = false;
  for (const { startMonth, level, changeRate } of bank.franchise.opexPrograms ?? []) {
    const c = changeRate ?? params.controllableOpexChangeRate[level];
    const age = month - startMonth;
    const achievedRate = achieved(c, changeRate, cutsSoFar, rho);
    phi *= 1 + (c < 0 ? achievedRate * (1 - creepBackShare(params.costCutChannels, level, age)) : achievedRate) * (age < 0 ? 0 : age < PHASE_IN_MONTHS ? PHASE_IN_SHARE : 1);
    if (c < 0) {
      cutsSoFar += 1;
      cutStartsNow ||= startMonth === month;
    }
  }
  return { phi, cutStartsNow };
}

/** E3a: one month of the price level, (1 + κ·CPI)^(1/12); the index at m is the index at m−1 times it. */
export const opexPriceStep = (params: OpexParams, cpiRate: number): number => (1 + (params.cpiPassThroughRate ?? 0) * cpiRate) ** (1 / 12);

/** E3: σ^ε · I, the scale the programme lines are paid at: the balance sheet at its real-growth elasticity while it grows, its downward one while it shrinks, and prices. */
const programmeScale = (params: OpexParams, sigma: number, priceIndex: number): number =>
  sigma ** (sigma >= 1 ? params.opexBalanceElasticityRate ?? 1 : params.opexDownwardElasticityRate ?? params.opexBalanceElasticityRate ?? 1) * priceIndex;

/** E9: the annual top-up, $, an Invest programme (a level in `investTechTopUpRateByLevel`) adds to the theme's spend in its first PHASE_IN_MONTHS months (the programmes' sum). */
function investTopUpAnnualUsd(programmesInForce: Programmes, params: OpexParams, themeSpendUsd: number, month: number): number {
  const rates = params.investTechTopUpRateByLevel;
  if (rates === undefined) return 0;
  let usd = 0;
  for (const { startMonth, level } of programmesInForce) {
    const age = month - startMonth;
    if (age >= 0 && age < PHASE_IN_MONTHS) usd += (rates[level as keyof typeof rates] ?? 0) * themeSpendUsd;
  }
  return usd;
}

/** E13: the operational losses a month, $: each programme's annual loss ÷ 12 for its first `operationalLossMonths`. */
function operationalLossUsd(programmesInForce: Programmes, channels: CostCutChannels | undefined, month: number): number {
  if (channels === undefined) return 0;
  let usd = 0;
  for (const { startMonth, level } of programmesInForce) {
    const age = month - startMonth;
    if (age >= 0 && age < channels.operationalLossMonths) usd += (channels.operationalLossAnnualUsdByLevel[level] ?? 0) / 12;
  }
  return usd;
}

/** E14: whether a programme holds the management rating one notch down at the quarter-end `month` (0 ≤ month − start < 3 × its level's quarters). */
export function managementNotchHeld(programmesInForce: Programmes | undefined, channels: CostCutChannels | undefined, month: number): boolean {
  if (channels === undefined) return false;
  for (const { startMonth, level } of programmesInForce ?? []) {
    const age = month - startMonth;
    if (age >= 0 && age < 3 * (channels.managementNotchQuartersByLevel[level] ?? 0)) return true;
  }
  return false;
}

/** The three opex lines that carry the one-time postings (07.3 E6, E7 and the event hit H), as `opex.oneOff.<kind>` in the ledger; their sum is `oneOffOpexUsd` in the month's P&L. No other line may carry the flag. */
export const ONE_OFF_OPEX_PREFIX = "oneOff.";
export const ONE_OFF_OPEX_LINES = { restructuring: `${ONE_OFF_OPEX_PREFIX}restructuring`, leaseExit: `${ONE_OFF_OPEX_PREFIX}leaseExit`, event: `${ONE_OFF_OPEX_PREFIX}event` } as const;

export const opexLines: OpexModel = {
  kind: "opex",
  monthly(call, { bank, fine, eventOpexUsd, capabilityEfficiencyRate }) {
    const { params } = call.ref;
    const { comp = 0, occupancy = 0, other = 0 } = params.baseLinesAnnualUsd ?? {};
    const cpb = params.costPerBranchAnnualUsd;
    const ops = fine.operations;
    const sigma = bank.franchise.opexBalanceScale ?? 1;
    const { phi, cutStartsNow } = programmes(bank, params, call.month);
    const keep = phi * (1 - capabilityEfficiencyRate) / 12;
    const scale = programmeScale(params, sigma, (bank.franchise.opexPriceIndex ?? 1) * opexPriceStep(params, call.env.cpiRate));

    const branchesVsStart = bank.franchise.branchCount + (bank.franchise.pendingOpenings ?? []).reduce((n, o) => n + o.count, 0) - params.branchCountAtSimStart;
    const branchRunningUsd = (branchesVsStart >= 0 ? 1 : (params.branchClosureSavingShare ?? 0)) * cpb * branchesVsStart;
    const roundStart = (call.month - 1) % MONTHS_PER_ROUND === 0;
    const branchesOpened = roundStart ? Math.max(0, ops.branchesDelta.value) : 0;
    const branchesClosed = roundStart ? Math.max(0, -ops.branchesDelta.value) : 0;

    let techUsd = 0;
    for (const [, spendUsd] of sortedEntries(ops.techInvestUsdByTheme.value)) techUsd += spendUsd ?? 0;
    const topUpUsd = investTopUpAnnualUsd(bank.franchise.opexPrograms ?? [], params, ops.techInvestUsdByTheme.value.efficiencyData ?? 0, call.month);
    const marketingUsd = [...fine.loans, ...fine.deposits].reduce((sum, p) => sum + p.marketingUsd.value, 0);
    const trainingDeltaUsd = ops.trainingUsd.value - (params.trainingBaseAnnualUsd ?? 0);
    const cdiRemainingUsd = bank.staticItemsUsd.intangibles_cdi ?? 0;
    const restructuringUsd = cutStartsNow ? ops.oneTimeChargeUsd.value : 0;

    const opexUsdByLine: Record<string, Usd> = {
      comp: roundToUsd((comp * (1 + ops.compChangeRate.value) + trainingDeltaUsd) * scale * keep),
      occupancy: roundToUsd((occupancy * scale + branchRunningUsd) * keep),
      other: roundToUsd(other * scale * keep),
      technology: roundToUsd(((techUsd + topUpUsd) * sigma) / 12),
      marketing: roundToUsd(marketingUsd / 12),
      cdi: roundToUsd(Math.min((params.cdiAmortizationAnnualUsd ?? 0) / 12, cdiRemainingUsd)),
      branchOpening: roundToUsd((params.branchOpeningCostMult ?? 0) * cpb * branchesOpened),
      events: roundToUsd(operationalLossUsd(bank.franchise.opexPrograms ?? [], params.costCutChannels, call.month)),
      // 10.1 E5a: the four non-recurring postings on three named lines the price adds back to the earnings it capitalises (`ONE_OFF_OPEX_PREFIX`): the event opex hit
      // (`opexHit` and its rate-of-opex kind both arrive as `eventOpexUsd`), the cut programme's restructuring charge R₁ (E6) and a closure's lease-exit charge (E7).
      [ONE_OFF_OPEX_LINES.event]: roundToUsd(eventOpexUsd),
      [ONE_OFF_OPEX_LINES.restructuring]: roundToUsd(restructuringUsd),
      [ONE_OFF_OPEX_LINES.leaseExit]: roundToUsd((params.branchLeaseExitChargePerBranchUsd ?? 0) * branchesClosed),
    };
    return { opexUsdByLine, restructuringUsd: roundToUsd(restructuringUsd) };
  },
};
